import { Injectable, inject } from '@angular/core';
import { Api } from 'src/app/core/api/api';
import { Token } from 'src/app/core/services/auth/token';
import { HttpService } from '../http/http.service';
import { delay } from 'src/games/common/helper';
import { AnalyticsService } from '../analytics/analytics.service';
import { getApp } from 'firebase/app';

@Injectable({
  providedIn: 'root',
})
export class FirebaseRealtimeDatabaseService {
  private httpService = inject(HttpService);
  private analyticsService = inject(AnalyticsService);
  private api: Api | undefined;
  private lastSetMovesTime: Map<string, number> = new Map();

  async getMoves(userId: string, roomId: string): Promise<string> {
    return this.get<string>(`moves/${roomId}/${userId}/moves`);
  }

  async setMoves(userId: string, roomId: string, moves: string): Promise<void> {
    const callTime = Date.now();
    this.lastSetMovesTime.set(roomId, callTime);
    await delay(500);
    if (this.lastSetMovesTime.get(roomId) !== callTime) {
      // Another setMoves function has started for this roomId
      return;
    }

    await this.patch<{ moves: string }, { moves: string }>(`moves/${roomId}/${userId}`, { moves });
  }

  private async get<T>(reference: string): Promise<T> {
    const api = await this.ensureApiInitialized();
    const token = await this.withFirebaseGuard(() => Token.get(), `RTDB get: ${reference}`);
    return api.get<T>(`${reference}.json?auth=${token}`, [2000]);
  }

  private async patch<T, V>(reference: string, body: V): Promise<T> {
    const api = await this.ensureApiInitialized();
    const token = await this.withFirebaseGuard(() => Token.get(), `RTDB patch: ${reference}`);
    return api.patch<T, V>(`${reference}.json?auth=${token}`, body, [2000]);
  }

  private async ensureApiInitialized(): Promise<Api> {
    if (!this.api) {
      const options = getApp().options;
      if (options.databaseURL) {
        this.api = new Api(options.databaseURL, this.httpService);
      } else {
        throw new Error('Database URL is not set');
      }
    }
    return this.api;
  }

  private async withFirebaseGuard<T>(operation: () => Promise<T>, context: string): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      this.analyticsService.handleError(error, context);
      throw error;
    }
  }
}
